/**
 * useDeviceSync — ported from v1.
 *
 * Runs inside AppLayout (within DatabaseProvider), so the DB is open
 * and auth is available.
 *
 * Responsibilities:
 * 1. Register device on mount with full device info (model, OS, timezone, etc.)
 * 2. Smart update: only send changed fields on subsequent syncs
 * 3. Fetch and store location data (loginLocation once, lastSeenLocation every 24h)
 * 4. Check force_logout at focus/online/foreground + push (no polling)
 * 5. On force_logout: update status to "logged_out" → hardLogout()
 * 6. Sync device capabilities at app-start, foreground, and reconnect (event-driven)
 * 7. Re-sync on Capacitor app becoming active (foreground)
 *
 * NEVER-01: force_logout is the ONLY automatic data-wiping mechanism,
 * and it was explicitly triggered by the user from another device.
 */

import { useEffect, useRef, useCallback } from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"
import { Capacitor } from "@capacitor/core"
import { db, type DbDevice } from "@/db/schema"
import {
  checkDeviceStatus,
  listDevices,
  updateDeviceStatusWithRetry,
  syncDevice,
} from "@/features/auth/services/deviceSync"
import {
  getStableDeviceId,
  initNativeDeviceId,
} from "@/shared/utils/device/stableDeviceId"
import { getFullDeviceInfo } from "@/shared/utils/device/deviceInfo"
import { fetchLocationData } from "@/shared/utils/device/locationData"
import {
  getDeviceCapabilitySnapshot,
  getBackgroundExecutionDiagnostics,
  toBackgroundDiagnosticsSnapshot,
  type CapabilitySnapshot,
  type BackgroundDiagnosticsSnapshot,
} from "@/shared/utils/device/devicePermissions"
import { useAuthStore } from "@/features/auth/stores/authStore"

dayjs.extend(utc)

/** Polling interval for force_logout check (ms). 5 min — force logout is rare. */
// REMOVED: Force-logout polling eliminated entirely. Checked at:
// - window.focus (web)
// - window.online (web)
// - Capacitor foreground (native) via Preferences flag
// - usePushSync (native) via push notification

// Capability sync interval removed (Fas 2) — capabilities synced via saveDevice at
// app-start, foreground, and reconnect. No standalone polling needed.

/**
 * Check if a force_logout push was received while the app was in the background (native only).
 * If the flag is set, verify with API and logout.
 */
async function checkNativeForceLogoutFlag(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const { Preferences } = await import("@capacitor/preferences")
    const { value } = await Preferences.get({ key: "force_logout_pending" })
    if (value !== "true") return
    // Clear flag immediately to avoid re-triggering
    await Preferences.remove({ key: "force_logout_pending" })
    console.warn("[DeviceSync] force_logout_pending flag found — verifying")
    const userId = getCurrentUserId()
    if (!userId) return
    const deviceId = getStableDeviceId(userId)
    const status = await checkDeviceStatus(deviceId)
    if (status === "force_logout") {
      console.warn("[DeviceSync] force_logout confirmed via API — logging out")
      await updateDeviceStatusWithRetry(deviceId, "logged_out", 3)
      window.location.href = "/logout"
    }
  } catch (e) {
    console.warn("[DeviceSync] checkNativeForceLogoutFlag error:", e)
  }
}

/** Hours before refreshing lastSeenLocation. */
const LOCATION_REFRESH_HOURS = 24

/**
 * Returns the current Dexie Cloud userId if authenticated, else null.
 * Checks both currentUserId AND currentUser.value.userId (which may be set
 * before currentUserId on some platforms like Capacitor/native).
 */
function getCurrentUserId(): string | null {
  try {
    // Primary: the getter on db.cloud
    const userId = db.cloud.currentUserId
    if (userId && userId !== "unauthorized") return userId

    // Fallback: check the observable value directly (may be set earlier on native)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentUser = (db.cloud.currentUser as any)?.value
    if (currentUser?.userId && currentUser.userId !== "unauthorized") {
      return currentUser.userId
    }

    return null
  } catch {
    return null
  }
}

/**
 * Returns true if the user is authenticated (not "unauthorized" or empty).
 */
function isAuthenticated(): boolean {
  return getCurrentUserId() !== null
}

export function useDeviceSync(): void {
  const isHandlingForceLogoutRef = useRef(false)
  const forceLogoutCompletedRef = useRef(false)
  const lastCheckedStatusRef = useRef<string | null>(null)
  const consecutiveFailuresRef = useRef(0)
  const lastCapabilitiesRef = useRef<CapabilitySnapshot | null>(null)
  const lastBackgroundDiagnosticsRef =
    useRef<BackgroundDiagnosticsSnapshot>(null)
  const isCapabilitySyncingRef = useRef(false)

  // ── Force logout check ──
  const checkForForceLogout = useCallback(async () => {
    if (!isAuthenticated()) return
    if (isHandlingForceLogoutRef.current) return
    if (forceLogoutCompletedRef.current) return
    // Skip during initial sync on new device
    if (useAuthStore.getState().initialSyncActive) return

    // Exponential backoff after consecutive failures (3 fails → skip next 3 polls, etc.)
    const failures = consecutiveFailuresRef.current
    if (failures >= 3) {
      // Skip this poll — reduce frequency progressively
      // After 3 fails: skip every other, after 6: skip 3 of 4, etc.
      const skipCount = Math.min(failures, 30) // cap at 30 = ~30 min between checks
      if (Math.random() * skipCount > 1) return
    }

    const userId = getCurrentUserId()
    if (!userId) return
    const deviceId = getStableDeviceId(userId)
    const status = await checkDeviceStatus(deviceId)

    if (!status) {
      consecutiveFailuresRef.current++
      if (consecutiveFailuresRef.current === 3) {
        console.warn("[DeviceSync] 3 consecutive failures — reducing poll frequency")
      }
      return
    }

    // Success — reset failure counter
    consecutiveFailuresRef.current = 0

    // Dedup — don't re-process the same status
    if (status === lastCheckedStatusRef.current) return
    lastCheckedStatusRef.current = status

    if (status === "force_logout") {
      isHandlingForceLogoutRef.current = true

      console.warn("[useDeviceSync] force_logout detected — wiping local data")

      // Confirm via a second check to guard against stale/transient responses
      const confirmed = await checkDeviceStatus(deviceId)
      if (confirmed !== "force_logout") {
        isHandlingForceLogoutRef.current = false
        return
      }

      // Update status to "logged_out" so it won't trigger again
      await updateDeviceStatusWithRetry(deviceId, "logged_out", 3)

      forceLogoutCompletedRef.current = true
      // Navigate to /logout page (outside DatabaseProvider) for clean wipe
      window.location.href = "/logout"
    }
  }, [])

  // ── Device save (register or smart update) — ported from v1 ──
  useEffect(() => {
    let isMounted = true

    const saveDevice = async () => {
      if (!isAuthenticated()) {
        console.log("[DeviceSync] saveDevice skipped — not authenticated")
        return
      }

      try {
        const userId = getCurrentUserId()
        if (!userId) return
        // Ensure native device ID is fetched from Keychain/AppSetId before
        // any synchronous getStableDeviceId() call (idempotent, caches to localStorage)
        await initNativeDeviceId(userId)
        const deviceId = getStableDeviceId(userId)
        console.log("[DeviceSync] saveDevice started, deviceId:", deviceId)
        const now = dayjs().utc().valueOf().toString()
        console.log("[DeviceSync] getting device info...")
        const deviceInfo = await getFullDeviceInfo()
        console.log("[DeviceSync] got device info")

        // Collect capability snapshot
        console.log("[DeviceSync] getting capabilities...")
        const capabilitySnapshot = await getDeviceCapabilitySnapshot()
        lastCapabilitiesRef.current = {
          pushPermission: capabilitySnapshot.pushPermission,
          alarmPermission: capabilitySnapshot.alarmPermission,
          localNotificationPermission:
            capabilitySnapshot.localNotificationPermission,
        }
        console.log("[DeviceSync] got capabilities")

        const backgroundDiagnostics = await getBackgroundExecutionDiagnostics()
        const backgroundSnapshot = toBackgroundDiagnosticsSnapshot(
          backgroundDiagnostics,
        )
        lastBackgroundDiagnosticsRef.current = backgroundSnapshot

        // Check if device already exists via API
        let existingDevice: DbDevice | undefined = undefined
        try {
          console.log("[DeviceSync] checking device status...")
          const statusRes = await checkDeviceStatus(deviceId)
          console.log("[DeviceSync] device status:", statusRes)
          if (statusRes) {
            // Device exists — fetch full data
            const devices = await listDevices()
            existingDevice = devices.find((d) => d.id === deviceId)
          }
        } catch {
          console.warn("[DeviceSync] Could not check existing device")
        }

        // Determine if we should fetch location data
        let shouldFetchLoginLocation = false
        let shouldUpdateLastSeenLocation = false

        if (!existingDevice) {
          shouldFetchLoginLocation = true
        } else if (existingDevice.lastSeenLocationAt) {
          const lastLocationMs = parseInt(existingDevice.lastSeenLocationAt, 10)
          const nowMs = dayjs().utc().valueOf()
          const hoursSince = (nowMs - lastLocationMs) / (1000 * 60 * 60)
          if (hoursSince >= LOCATION_REFRESH_HOURS) {
            shouldUpdateLastSeenLocation = true
          }
        } else {
          shouldUpdateLastSeenLocation = true
        }

        // Fetch location in parallel with a timeout — don't block device registration
        let locationData: string | undefined = undefined
        if (shouldFetchLoginLocation || shouldUpdateLastSeenLocation) {
          console.log("[DeviceSync] fetching location...")
          locationData = await fetchLocationData()
          console.log("[DeviceSync] location:", locationData ? "ok" : "unavailable")
        }

        if (!existingDevice) {
          // ── NEW device ──
          const newDevice: Partial<DbDevice> = {
            id: deviceId,
            ...deviceInfo,
            createdAt: now,
            lastSeenAt: now,
            isDeleted: 0,
            status: "active",
            loginLocation: locationData,
            lastSeenLocation: locationData,
            lastSeenLocationAt: locationData ? now : undefined,
            pushPermission: capabilitySnapshot.pushPermission,
            alarmPermission: capabilitySnapshot.alarmPermission,
            localNotificationPermission:
              capabilitySnapshot.localNotificationPermission,
            capabilitiesUpdatedAt: now,
            ...(backgroundSnapshot
              ? {
                  notificationsEnabled: backgroundSnapshot.notificationsEnabled,
                  androidCanScheduleExactAlarms:
                    backgroundSnapshot.androidCanScheduleExactAlarms,
                  androidIsBackgroundRestricted:
                    backgroundSnapshot.androidIsBackgroundRestricted,
                  iosBackgroundRefreshStatus:
                    backgroundSnapshot.iosBackgroundRefreshStatus,
                  backgroundExecutionUpdatedAt: now,
                }
              : undefined),
          }

          console.log("[DeviceSync] registering NEW device...")
          await syncDevice(newDevice, true)
        } else {
          // ── EXISTING device — smart update (only changed fields) ──
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updateObj: Record<string, any> = {}

          // Always update lastSeenAt and ensure active status
          updateObj.lastSeenAt = now
          if (existingDevice.status !== "active") {
            updateObj.status = "active"
            updateObj.statusAt = now
            updateObj.isDeleted = 0
          }

          // App version
          if (existingDevice.appVersion !== deviceInfo.appVersion) {
            updateObj.appVersion = deviceInfo.appVersion
          }
          if (
            deviceInfo.appBuild &&
            existingDevice.appBuild !== deviceInfo.appBuild
          ) {
            updateObj.appBuild = deviceInfo.appBuild
          }

          // Location
          if (shouldUpdateLastSeenLocation && locationData) {
            updateObj.lastSeenLocation = locationData
            updateObj.lastSeenLocationAt = now
            if (!existingDevice.loginLocation) {
              updateObj.loginLocation = locationData
            }
          }

          // Device info fields — only update if changed
          if (existingDevice.platform !== deviceInfo.platform)
            updateObj.platform = deviceInfo.platform
          if (existingDevice.model !== deviceInfo.model)
            updateObj.model = deviceInfo.model
          if (existingDevice.userAgent !== deviceInfo.userAgent)
            updateObj.userAgent = deviceInfo.userAgent
          if (existingDevice.operatingSystem !== deviceInfo.operatingSystem)
            updateObj.operatingSystem = deviceInfo.operatingSystem
          if (existingDevice.osVersion !== deviceInfo.osVersion)
            updateObj.osVersion = deviceInfo.osVersion
          if (existingDevice.iOSVersion !== deviceInfo.iOSVersion)
            updateObj.iOSVersion = deviceInfo.iOSVersion
          if (existingDevice.androidSDKVersion !== deviceInfo.androidSDKVersion)
            updateObj.androidSDKVersion = deviceInfo.androidSDKVersion
          if (existingDevice.manufacturer !== deviceInfo.manufacturer)
            updateObj.manufacturer = deviceInfo.manufacturer
          if (existingDevice.isVirtual !== deviceInfo.isVirtual)
            updateObj.isVirtual = deviceInfo.isVirtual
          if (existingDevice.webViewVersion !== deviceInfo.webViewVersion)
            updateObj.webViewVersion = deviceInfo.webViewVersion
          if (existingDevice.language !== deviceInfo.language)
            updateObj.language = deviceInfo.language
          if (existingDevice.timezone !== deviceInfo.timezone)
            updateObj.timezone = deviceInfo.timezone

          // Capabilities
          let capabilitiesChanged = false
          if (
            existingDevice.pushPermission !== capabilitySnapshot.pushPermission
          ) {
            updateObj.pushPermission = capabilitySnapshot.pushPermission
            capabilitiesChanged = true
          }
          if (
            existingDevice.alarmPermission !==
            capabilitySnapshot.alarmPermission
          ) {
            updateObj.alarmPermission = capabilitySnapshot.alarmPermission
            capabilitiesChanged = true
          }
          if (
            existingDevice.localNotificationPermission !==
            capabilitySnapshot.localNotificationPermission
          ) {
            updateObj.localNotificationPermission =
              capabilitySnapshot.localNotificationPermission
            capabilitiesChanged = true
          }
          if (capabilitiesChanged || !existingDevice.capabilitiesUpdatedAt) {
            updateObj.capabilitiesUpdatedAt = now
          }

          // Background execution diagnostics
          if (backgroundSnapshot) {
            let backgroundChanged = false

            if (
              existingDevice.notificationsEnabled !==
              backgroundSnapshot.notificationsEnabled
            ) {
              updateObj.notificationsEnabled =
                backgroundSnapshot.notificationsEnabled
              backgroundChanged = true
            }
            if (
              existingDevice.androidCanScheduleExactAlarms !==
              backgroundSnapshot.androidCanScheduleExactAlarms
            ) {
              updateObj.androidCanScheduleExactAlarms =
                backgroundSnapshot.androidCanScheduleExactAlarms
              backgroundChanged = true
            }
            if (
              existingDevice.androidIsBackgroundRestricted !==
              backgroundSnapshot.androidIsBackgroundRestricted
            ) {
              updateObj.androidIsBackgroundRestricted =
                backgroundSnapshot.androidIsBackgroundRestricted
              backgroundChanged = true
            }
            if (
              existingDevice.iosBackgroundRefreshStatus !==
              backgroundSnapshot.iosBackgroundRefreshStatus
            ) {
              updateObj.iosBackgroundRefreshStatus =
                backgroundSnapshot.iosBackgroundRefreshStatus
              backgroundChanged = true
            }
            if (
              backgroundChanged ||
              !existingDevice.backgroundExecutionUpdatedAt
            ) {
              updateObj.backgroundExecutionUpdatedAt = now
            }
          }

          if (Object.keys(updateObj).length > 0) {
            await syncDevice({ id: deviceId, ...updateObj }, false)
          }
        }
      } catch (err) {
        console.warn("[DeviceSync] Could not save device info via API", err)
      }
    }

    // ── Capability sync (runs every 20s, only sends if changed) ──
    const syncCapabilitiesIfChanged = async () => {
      if (!isAuthenticated()) return
      if (isCapabilitySyncingRef.current) return
      // Skip during initial sync on new device
      if (useAuthStore.getState().initialSyncActive) return
      // Back off if force-logout check is failing (likely auth issue)
      if (consecutiveFailuresRef.current >= 3) return
      isCapabilitySyncingRef.current = true

      try {
        const snapshot = await getDeviceCapabilitySnapshot()
        const last = lastCapabilitiesRef.current

        const backgroundDiagnostics = await getBackgroundExecutionDiagnostics()
        const backgroundSnapshot = toBackgroundDiagnosticsSnapshot(
          backgroundDiagnostics,
        )
        const lastBackground = lastBackgroundDiagnosticsRef.current

        const capabilitiesChanged =
          !last ||
          last.pushPermission !== snapshot.pushPermission ||
          last.alarmPermission !== snapshot.alarmPermission ||
          last.localNotificationPermission !==
            snapshot.localNotificationPermission

        const backgroundChanged =
          JSON.stringify(lastBackground ?? null) !==
          JSON.stringify(backgroundSnapshot ?? null)

        if (!capabilitiesChanged && !backgroundChanged) return

        const userId = getCurrentUserId()
        if (!userId) return
        const deviceId = getStableDeviceId(userId)
        const now = dayjs().utc().valueOf().toString()

        const updateObj: Partial<DbDevice> = {
          id: deviceId,
          ...(capabilitiesChanged
            ? {
                pushPermission: snapshot.pushPermission,
                alarmPermission: snapshot.alarmPermission,
                localNotificationPermission:
                  snapshot.localNotificationPermission,
                capabilitiesUpdatedAt: now,
              }
            : undefined),
          ...(backgroundChanged && backgroundSnapshot
            ? {
                notificationsEnabled: backgroundSnapshot.notificationsEnabled,
                androidCanScheduleExactAlarms:
                  backgroundSnapshot.androidCanScheduleExactAlarms,
                androidIsBackgroundRestricted:
                  backgroundSnapshot.androidIsBackgroundRestricted,
                iosBackgroundRefreshStatus:
                  backgroundSnapshot.iosBackgroundRefreshStatus,
                backgroundExecutionUpdatedAt: now,
              }
            : undefined),
        }

        const success = await syncDevice(updateObj, false)
        if (success) {
          lastCapabilitiesRef.current = {
            pushPermission: snapshot.pushPermission,
            alarmPermission: snapshot.alarmPermission,
            localNotificationPermission: snapshot.localNotificationPermission,
          }
          lastBackgroundDiagnosticsRef.current = backgroundSnapshot
        }
      } catch (error) {
        console.warn("[DeviceSync] Failed to sync capabilities", error)
      } finally {
        isCapabilitySyncingRef.current = false
      }
    }

    // ── Trigger functions ──
    const triggerSave = () => {
      if (!isMounted) return
      void saveDevice()
    }

    // Wait for auth to be ready AND initialSyncActive to clear before
    // triggering device save. On native, initialSyncActive stays true for
    // the duration of the initial data download on a new device.
    let authCheckInterval: ReturnType<typeof setInterval> | null = null
    let authResolved = false

    const tryTriggerWhenReady = () => {
      if (authResolved || !isMounted) return
      if (!isAuthenticated()) return
      // Wait for initialSyncActive to clear (set false after initial sync)
      if (useAuthStore.getState().initialSyncActive) return

      authResolved = true
      if (authCheckInterval) clearInterval(authCheckInterval)
      console.log("[DeviceSync] auth ready + guard clear — triggering saveDevice")
      triggerSave()
    }

    // Subscribe to currentUser observable for immediate notification on auth
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const observable = db.cloud.currentUser as any
    let authCleanup: (() => void) | null = null
    if (observable?.subscribe) {
      const sub = observable.subscribe(
        (value: { userId?: string; accessToken?: string } | null) => {
          if (value?.userId && value.userId !== "unauthorized" && !authResolved) {
            tryTriggerWhenReady()
          }
        },
      )
      if (typeof sub === "function") {
        authCleanup = sub
      } else if (sub && typeof sub.unsubscribe === "function") {
        authCleanup = () => sub.unsubscribe()
      }
    }

    // Subscribe to authStore changes (initialSyncActive → false triggers)
    const unsubAuthStore = useAuthStore.subscribe((state) => {
      if (!state.initialSyncActive && !authResolved) {
        tryTriggerWhenReady()
      }
    })

    // Check every 2s for up to 60s whether both conditions are met
    authCheckInterval = setInterval(tryTriggerWhenReady, 2000)
    // Also try immediately and at early points
    setTimeout(tryTriggerWhenReady, 500)
    const registerTimer = setTimeout(tryTriggerWhenReady, 3000)
    const retryTimer = setTimeout(tryTriggerWhenReady, 15_000)
    // Hard stop: give up checking after 60s (was 30s — extend for slow native sync)
    const authGiveUpTimer = setTimeout(() => {
      if (authCheckInterval) clearInterval(authCheckInterval)
      if (!authResolved) {
        console.warn("[DeviceSync] auth/guard never became ready within 60s")
        // Last-ditch attempt — force save even if initialSyncActive is stuck
        if (isAuthenticated()) {
          console.log("[DeviceSync] force-triggering saveDevice after timeout")
          authResolved = true
          triggerSave()
        }
      }
    }, 60_000)
    void syncCapabilitiesIfChanged()

    // Force-logout polling removed — checked at focus/online/foreground + push

    // Capability polling removed (Fas 2) — synced via saveDevice + foreground

    // Window events — minimal footprint (Fas 4)
    const handleFocus = () => {
      void checkForForceLogout()
      // Dexie Cloud sync is triggered automatically by the addon on focus
    }
    const handleOnline = () => {
      // On reconnect: save device (includes capabilities) + check force-logout
      void checkForForceLogout()
      triggerSave()
    }
    window.addEventListener("focus", handleFocus)
    window.addEventListener("online", handleOnline)

    // App foreground listener (via centralized hub — web + native)
    // saveDevice() already includes capability sync (Fas 2+4)
    const removeForeground = appLifecycleHub.on("foreground", () => {
      triggerSave()
      // Check for force_logout flag set by native push handler (Fas 1)
      void checkNativeForceLogoutFlag()
    })

    return () => {
      isMounted = false
      clearTimeout(registerTimer)
      clearTimeout(retryTimer)
      clearTimeout(authGiveUpTimer)
      if (authCheckInterval) clearInterval(authCheckInterval)
      authCleanup?.()
      unsubAuthStore()
      window.removeEventListener("focus", handleFocus)
      window.removeEventListener("online", handleOnline)
      removeForeground()
    }
  }, [checkForForceLogout])
}
